import { useState } from "react";
import StackKit from "@/shared/components/kit/stack";
import {CircularProgress,Grid,TextField,} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ColumnCharts from "./chart";
import CustomSelect from "@/shared/components/common/select";
import CustomButton from "@/shared/components/kit/button";
import CustomTable from "@/shared/components/common/table";
import AdapterMomentJalali from "@/shared/components/common/datePeacker";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import BoxKit from "@/shared/components/kit/box";
import { numberSeparator, toPersianNumber,} from "@/hooks/useThousandsSeparator";
import { fetchListChartCashTransactionList } from "@/api/merchant/request.get";
import moment from "jalali-moment";
/**
 * Content
 *
 * نمایش لیست تراکنش‌ها همراه با نمودار و قابلیت جستجو و فیلتر.
 * این کامپوننت واکنش‌گرا بوده و در اندازه‌های کوچک صفحه، فرم‌ها و عناصر جدول را به صورت عمودی نمایش می‌دهد.
 *
 * @param {Object} props
 * @param {any} props.dataTable - داده‌های جدول شامل تراکنش‌ها و اطلاعات متا.
 * @param {string | number | any} [props.selectedNumber] - شماره انتخاب شده یا شناسه مربوطه برای فیلتر کردن تراکنش‌ها.
 * @param {number} props.page - شماره صفحه فعلی برای صفحه‌بندی جدول.
 * @param {(page: number) => void} props.setPage - تابع برای تغییر صفحه جدول.
 *
 * @returns {JSX.Element} کامپوننت Content شامل نمودار، فیلترها و جدول تراکنش‌ها.
 *
 * ویژگی‌ها:
 * - واکنش‌گرایی با Grid و StackKit
 * - فیلتر بر اساس تعداد تراکنش‌ها
 * - جستجوی متنی تراکنش‌ها
 * - نمایش نمودار ستونی تراکنش‌ها
 * - پشتیبانی از شماره کارت و مبدا/مقصد تراکنش
 */

const Content = ({dataTable,selectedNumber,page,setPage}: {dataTable: any;selectedNumber?: string | number | any;setPage: any;page: number;}) => {
  const refTypeMap: Record<string, string> = {
    transfer: "انتقال",
    order: "سفارش",
    "pro-ref-earn": "کارمزد چند سطحی حرفه ای",
    "ref-earn": "کارمزد تک سطحی",
    withdraw: "برداشت",
    installments: "اقساط",
    deposite: "افزایش موجودی",
    creditScoreFee: "کارمزد اعتبار سنجی",
    resellerOrder: "-",
    bistCard: "ساعی پی",
    edit: "ویرایش",
    "pos-pazirande": "دستگاه پوز",
    "pos-ghishe": "-",
    wallet: "کیف پول",
    ipg: "Ipg",
    "edit-pazirande": "اصلاح توسط پذیرنده",
    gift_fee: "-",
    emv: "خرید emv",
    qr_code: "QR Code",
  };

  const [value, setValue] = useState(10);
  const [selected, setSelected] = useState<any>("weekly");
  const [activeTab, setActiveTab] = useState("هفتگی");

  const { data: dataListChart } = useQuery({
    queryKey: ["ListPazirandeClubsChart-page-data", selectedNumber, selected, page],
    queryFn: () =>
      fetchListChartCashTransactionList(
        selectedNumber === "all" ? "charge_merchant_wallet" : selectedNumber,
        selected,
        page
      ),
  });

  const handleChange = (val: any) => setValue(val);

  const dataHeader = [
    { id: "rowId", title: "ردیف" },
    { id: "amount", title: "مبلغ ریال" },
    { id: "ts", title: "تاریخ و ساعت" },
    { id: "source_card_number", title: "مبدا تراکنش" },
    { id: "destination_number", title: "مقصد تراکنش" },
    { id: "ref_id", title: "کد رهگیری تراکنش" },
    { id: "description", title: "توضیحات" },
    ...(selectedNumber === "cash_buy_application"
      ? [{ id: "ref_type", title: "نوع تراکنش" }]
      : []),
  ];

  const newDataTable = dataTable?.transactions?.map((item: any, index: number) => {
    if (!item) return null;
    return {
      ...item,
      ref_id: toPersianNumber(`${item.ref_id}`),
      rowId: toPersianNumber(index + 1),
      ts: item?.ts ? ` ${toPersianNumber(moment.unix(item?.ts).locale("fa").format("HH:mm"))} ${toPersianNumber(moment.unix(item?.ts).locale("fa").format("YYYY-MM-DD"))}` : "-",
      source_card_number: item.source_card_number ? <TypoGraphyKit sx={{ direction: "ltr" }}>{toPersianNumber(item.source_card_number)}</TypoGraphyKit> : "-",
      destination_number: toPersianNumber("کیف پول 20 کارت"),
      amount: <BoxKit>{item?.amount ? numberSeparator(item.amount) : "۰"}</BoxKit>,
      description: item?.description ? toPersianNumber(`${item.description}`) : "-",
      ...(selectedNumber === "cash_buy_application" && { ref_type: item?.ref_type ? refTypeMap[item?.ref_type] : "-" }),
    };
  });

  if (!dataListChart) {
    return (
      <div className="min-h-[300px] flex justify-center items-center">
        <CircularProgress size={24} />
      </div>
    );
  }
  
  return (
    <StackKit sx={{ width: "100%" }} gap={3}>
      <ColumnCharts
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        chartData={dataListChart?.transactions}
        colors={["linear-gradient(180deg, #EB6440 0%, #853924 100%)","#4ECDC4"]}
        setSelected={setSelected}
        colorSideBar="#fff"
      />
      <Grid container spacing={2}>          
          <CustomSelect
            label={<TypoGraphyKit variant="body1" sx={{ color: "#A4A4A4" }}>تراکنش آخر</TypoGraphyKit>}
            options={[5, 10, 15, 20]}
            onChange={handleChange}
            value={value || 5}
          />
        <Grid size={{xs:12 , md:6}}>
          <StackKit
            direction={{ xs: "column", sm: "row" }}
            gap={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <AdapterMomentJalali handleChange={() => {}} heightProps="45px" />
            <TextField fullWidth placeholder="وارد کردن جزئیات جستجو" />
            <CustomButton sx={{ minWidth: 120 }}>
              <TypoGraphyKit variant="body1">جستجو</TypoGraphyKit>
            </CustomButton>
          </StackKit>
        </Grid>
        <Grid size={{xs:12}}>
          <CustomTable
            dataHeader={dataHeader}
            dataTbody={newDataTable}
            total={dataTable?.meta?.total}
            setPages={setPage}
            currentPage={page}
          />
        </Grid>
      </Grid>
    </StackKit>
  );
};

export default Content;
